import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { filter, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { getProduct } from '../actions/product.action';
import { selectProductFromDictionary } from '../selectors/product.selector';
import {
  BoelsProductReference,
  BOProduct,
  ProductReference,
  RedirectModel,
} from '@boels/models';
import { ProductGatewayService } from '../gateway/product.gateway';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { RoutingService } from '@boels/helpers';
import { CultureFacade } from '../../cultures/facade/cultures.facade';

@Injectable({
  providedIn: 'root',
})
export class ProductFacade {
  #store = inject(Store);
  #productGatewayService = inject(ProductGatewayService);
  #routingService = inject(RoutingService);
  #cultureFacade = inject(CultureFacade);

  get(productCode: string): Observable<BOProduct | undefined> {
    return of(productCode).pipe(
      distinctUntilChanged(),
      switchMap((productCode) =>
        this.#cultureFacade.getActiveCulture().pipe(
          filter(Boolean),
          take(1),
          tap((culture) =>
            this.#store.dispatch(getProduct({ productCode, culture })),
          ),
          switchMap((culture) =>
            this.#store.pipe(
              select(selectProductFromDictionary({ productCode, culture })),
            ),
          ),
        ),
      ),
      shareReplay(1),
    );
  }

  getProductReferences(
    productCode: string,
    referenceType?: string,
    pageSize?: number,
  ): Observable<ProductReference[]> {
    return this.#productGatewayService
      .getProductReferences(productCode, referenceType, pageSize)
      .pipe(shareReplay(1));
  }

  private concatAccessories(
    accessories: Observable<[BoelsProductReference[], BoelsProductReference[]]>,
  ): Observable<BoelsProductReference[]> {
    return accessories.pipe(
      map(([hireAccessories, saleAccessories]) => [
        ...hireAccessories,
        ...saleAccessories,
      ]),
      map((productReferences) => productReferences.filter(Boolean)),
    );
  }

  /**
   * Returns an observable for the current product
   * @returns Product
   * @returns undefined if product can't be found
   */
  getCurrentProduct(): Observable<BOProduct | undefined> {
    const productCode = this.#routingService.getRoutePropertyWithValue<
      string,
      'params'
    >('params', 'productCode');
    return productCode ? this.get(productCode) : of(undefined);
  }

  getCurrentProductAsObservable(): Observable<BOProduct | undefined> {
    const productCode =
      this.#routingService.getRoutePropertyWithValueAsObservable<
        string,
        'params'
      >('params', 'productCode');
    return productCode.pipe(
      switchMap((productCode) =>
        productCode ? this.get(productCode) : of(undefined),
      ),
    );
  }

  getRedirectForProduct(productCode: string): Observable<RedirectModel> {
    return this.#productGatewayService.getRedirectForProduct(productCode);
  }
}
